import { Avatar, Box, List, ListItem, ListItemText, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getTeamImage } from '../../../models/TeamConstants';
import { getCorrectForPeriod, type LeaderboardPeriod } from '../../../hooks/useLeaderboardData';
import type { User } from '../../../models/User';

interface LeaderboardPanelProps {
  title: string;
  entries: User[];
  highlightUserId?: string;
  activePeriod: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
}

const PERIOD_LABELS: Record<LeaderboardPeriod, string> = {
  week: 'Week',
  month: 'Month',
  alltime: 'All Time',
};

export default function LeaderboardPanel({ title, entries, highlightUserId, activePeriod, onPeriodChange }: LeaderboardPanelProps) {
  const theme = useTheme();

  return (
    <>
      {/* Title row with period toggle */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4">
          {title}
        </Typography>
        <ToggleButtonGroup
          value={activePeriod}
          exclusive
          onChange={(_, value) => { if (value) onPeriodChange(value); }}
          size="small"
        >
          {(Object.keys(PERIOD_LABELS) as LeaderboardPeriod[]).map(period => (
            <ToggleButton key={period} value={period}>
              {PERIOD_LABELS[period]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {entries.length === 0 ? (
        <Typography align="center" color="text.secondary">
          No scores yet
        </Typography>
      ) : (
        <Paper>
          <List>
            {entries.map((entry, index) => {
              const correct = getCorrectForPeriod(entry, activePeriod);
              const points = correct * 10;
              return (
                <ListItem
                  key={entry.userId}
                  divider
                  sx={{
                    ...(entry.userId === highlightUserId && { backgroundColor: 'rgba(255,255,255,0.08)' }),
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.25)' },
                  }}
                >
                  <Avatar
                    src={entry.avatarUrl ?? undefined}
                    alt={entry.username}
                    sx={{
                      width: 50,
                      height: 50,
                      border: `2px solid ${theme.palette.primary.main}`,
                    }}
                  />

                  <Typography variant="h5" sx={{ mx: 2 }} />

                  <ListItemText
                    primary={`${index + 1})  ${entry.username} : ${correct} correct answers : ${points} pts`}
                    secondary={`Team: ${entry.team || 'Unsorted'}`}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Typography variant="h1" sx={{ fontSize: '4rem', lineHeight: 1 }}>
                      {getTeamImage(entry.team)}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
    </>
  );
}